import type { ReactNode } from "react";
import { useMemo } from "react";

import { Flex } from "@similarweb/ui-components/foundations";

import type { ChipItem } from "app/pages/sales-intelligence/common-components/filters-panel";
import type { IFilterViewBaseProps } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import { LimitedChipItems } from "app/pages/sales-intelligence/common-components/filters-panel";
import { MAX_CHIPS_GROUP_AMOUNT } from "app/pages/sales-intelligence/common-components/filters-panel/constants";
import useIabCategoriesById from "app/pages/sales-intelligence/modules/find-apps-filters/components/filters/IabCategoriesFilter/hooks/useIabCategoriesById";
import type IabCategoriesFilter from "app/pages/sales-intelligence/modules/find-apps-filters/models/IabCategoriesFilter/IabCategoriesFilter";

const IabCategoriesFilterResult = ({
    value,
    filter,
    isExpanded,
    onFilterUpdate,
}: IFilterViewBaseProps<IabCategoriesFilter>): ReactNode => {
    const optionsById = useIabCategoriesById(filter.options);

    const chipItems: readonly ChipItem[] = useMemo(() => {
        if (!value?.values?.length) {
            return [];
        }

        return value.values.map((id) => ({
            id,
            text: optionsById[id].name,
        }));
    }, [value, optionsById]);

    const handleOnChipClose = (chipId: string) => {
        if (!value?.values?.length) {
            return;
        }

        const values = value.values.filter((id) => id !== chipId);

        onFilterUpdate(
            values.length
                ? {
                      ...value,
                      values: value.values.filter((id) => id !== chipId),
                  }
                : undefined,
        );
    };

    if (!value?.values?.length) {
        return null;
    }

    return (
        <Flex flexWrap="wrap">
            <LimitedChipItems
                items={chipItems}
                onDelete={handleOnChipClose}
                maxItems={MAX_CHIPS_GROUP_AMOUNT}
                isTabExpanded={isExpanded}
            />
        </Flex>
    );
};

export default IabCategoriesFilterResult;
